import { Injectable, OnDestroy, inject } from '@angular/core';
import { BehaviorSubject, Subject, combineLatest, of } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { MessagingContextService } from './messaging-context.service';
import { MessagingNotificationsService } from './messaging-notifications.service';
import { MessagingRealtimeService } from './messaging-realtime.service';
import { MessagingThreadsService } from './messaging-threads.service';
import {
  EnsureThreadRequest,
  MarkNotificationOpenedRequest,
  MarkNotificationReadRequest,
  MarkThreadReadRequest,
  MessagingCounts,
  MessagingNotification,
  MessagingParticipant,
  MessagingThreadSummary,
  NotificationListFilters,
  ThreadListFilters
} from './messaging.models';

@Injectable({ providedIn: 'root' })
export class MessagingStoreService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private bootstrapped = false;
  private readonly contextService = inject(MessagingContextService);
  private readonly threadsService = inject(MessagingThreadsService);
  private readonly notificationsService = inject(MessagingNotificationsService);
  private readonly realtimeService = inject(MessagingRealtimeService);

  private readonly threadsSubject = new BehaviorSubject<MessagingThreadSummary[]>([]);
  private readonly notificationsSubject = new BehaviorSubject<MessagingNotification[]>([]);
  private readonly countsSubject = new BehaviorSubject<MessagingCounts>({
    unreadThreadCount: 0,
    unreadNotificationCount: 0
  });
  // Audio player for incoming notification sound
  private readonly notificationAudio = new Audio('/assets/sounds/notification.mp3');
  private previousNotificationIds = new Set<string>();
  private notificationsInitialized = false;
  private readonly activeThreadIdSubject = new BehaviorSubject<string | null>(null);

  readonly threads$ = this.threadsSubject.asObservable();
  readonly notifications$ = this.notificationsSubject.asObservable();
  readonly counts$ = this.countsSubject.asObservable();
  readonly activeThreadId$ = this.activeThreadIdSubject.asObservable();
  readonly activeThreadUnreadCount$ = combineLatest([this.threads$, this.activeThreadId$]).pipe(
    map(([threads, activeThreadId]) => {
      if (!activeThreadId) return 0;
      return threads.find((thread) => thread.threadId === activeThreadId)?.unreadCount ?? 0;
    })
  );

  bootstrap(filters?: { threadFilters?: Partial<ThreadListFilters>; notificationFilters?: Partial<NotificationListFilters> }): void {
    if (this.bootstrapped) {
      return;
    }

    this.bootstrapped = true;
    const context = this.contextService.getCurrentContext();
    console.log('[MessagingStore] bootstrap called for context:', context);

    this.loadThreads(filters?.threadFilters);
    this.loadNotifications(filters?.notificationFilters);
    this.loadCounts();

    this.realtimeService.watchThreadIndex(context)
      .pipe(takeUntil(this.destroy$))
      .subscribe((threads) => {
        if (threads.length) {
          this.threadsSubject.next(this.sortThreads(threads));
        }
      });

    this.realtimeService.watchNotifications(context)
      .pipe(takeUntil(this.destroy$))
      .subscribe((notifications) => {
        if (notifications.length) {
          const sorted = this.sortNotifications(notifications);

          // Play a sound for newly arrived unread notifications (skip initial load)
          try {
            if (this.notificationsInitialized) {
              const newUnread = sorted.filter(n => (n.status ?? '') !== 'read' && !this.previousNotificationIds.has(n.notificationId));
              if (newUnread.length) {
                // reset and play; swallow promise rejection (autoplay may be blocked)
                try { this.notificationAudio.currentTime = 0; } catch {}
                this.notificationAudio.play()?.catch(() => {});
              }
            }
          } catch (err) {
            console.warn('Notification sound failed', err);
          }

          this.previousNotificationIds = new Set((sorted || []).map(n => n.notificationId));
          this.notificationsInitialized = true;

          this.notificationsSubject.next(sorted);
        }
      });

    this.realtimeService.watchCounts(context)
      .pipe(takeUntil(this.destroy$))
      .subscribe((counts) => {
        if (counts) {
          this.countsSubject.next(counts);
        }
      });
  }

  loadThreads(overrides?: Partial<ThreadListFilters>): void {
    const context = this.contextService.getCurrentContext();
    const filters: ThreadListFilters = {
      organizationId: context.organizationId,
      ...overrides
    };

    this.threadsService.listThreads(context.participantId, filters)
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          console.error('Failed to load messaging threads', error);
          return of(null);
        })
      )
      .subscribe((response) => {
        if (!response) return;
        this.threadsSubject.next(this.sortThreads(response.data ?? []));
        this.countsSubject.next(response.counts ?? this.countsSubject.getValue());
      });
  }

  loadNotifications(overrides?: Partial<NotificationListFilters>): void {
    const context = this.contextService.getCurrentContext();
    const filters: NotificationListFilters = {
      organizationId: context.organizationId,
      ...overrides
    };

    this.notificationsService.listNotifications(context.participantId, filters)
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          console.error('Failed to load messaging notifications', error);
          return of(null);
        })
      )
      .subscribe((response) => {
        if (!response) return;
        this.notificationsSubject.next(this.sortNotifications(response.data ?? []));
        this.countsSubject.next(response.counts ?? this.countsSubject.getValue());
      });
  }

  loadCounts(): void {
    const context = this.contextService.getCurrentContext();

    this.notificationsService.getCounts(context.participantId, context.organizationId)
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          console.error('Failed to load messaging counts', error);
          return of(null);
        })
      )
      .subscribe((counts) => {
        if (counts) {
          this.countsSubject.next(counts);
        }
      });
  }

  ensureThread(payload: Omit<EnsureThreadRequest, 'organizationId' | 'sender'> & { sender?: MessagingParticipant }) {
    const context = this.contextService.getCurrentContext();
    const sender = payload.sender ?? this.contextService.getCurrentParticipant();

    return this.threadsService.ensureThread({
      ...payload,
      organizationId: context.organizationId,
      sender
    });
  }

  notifyThreadMessage(threadId: string, payload: Parameters<MessagingThreadsService['notifyThreadMessage']>[1]) {
    return this.threadsService.notifyThreadMessage(threadId, payload);
  }

  markThreadRead(threadId: string, partial: Partial<MarkThreadReadRequest> = {}) {
    const context = this.contextService.getCurrentContext();
    const payload: MarkThreadReadRequest = {
      organizationId: context.organizationId,
      participantId: context.participantId,
      lastReadAt: partial.lastReadAt ?? new Date().toISOString(),
      ...(partial.lastReadMessageId ? { lastReadMessageId: partial.lastReadMessageId } : {})
    };

    return this.threadsService.markThreadRead(threadId, payload);
  }

  markNotificationOpened(notificationId: string, partial: Partial<MarkNotificationOpenedRequest> = {}) {
    console.log(`[MessagingStore] markNotificationOpened called for notificationId=${notificationId} with partial:`, partial);
    const context = this.contextService.getCurrentContext();
    const payload: MarkNotificationOpenedRequest = {
      organizationId: context.organizationId,
      participantId: context.participantId,
      openedAt: partial.openedAt ?? new Date().toISOString()
    };

    return this.notificationsService.markOpened(notificationId, payload);
  }

  markNotificationRead(notificationId: string, partial: Partial<MarkNotificationReadRequest> = {}) {
    console.log(`[MessagingStore] markNotificationRead called for notificationId=${notificationId} with partial:`, partial);
    const context = this.contextService.getCurrentContext();
    const payload: MarkNotificationReadRequest = {
      organizationId: context.organizationId,
      participantId: context.participantId,
      readAt: partial.readAt ?? new Date().toISOString()
    };

    return this.notificationsService.markRead(notificationId, payload);
  }

  setActiveThread(threadId: string | null): void {
    this.activeThreadIdSubject.next(threadId);
  }

  private sortThreads(threads: MessagingThreadSummary[]): MessagingThreadSummary[] {
    return [...threads].sort((left, right) => {
      const leftTs = new Date(left.lastMessageAt ?? left.updatedAt ?? 0).getTime();
      const rightTs = new Date(right.lastMessageAt ?? right.updatedAt ?? 0).getTime();
      return rightTs - leftTs;
    });
  }

  private sortNotifications(notifications: MessagingNotification[]): MessagingNotification[] {
    return [...notifications].sort((left, right) => {
      const leftTs = new Date(left.createdAt ?? 0).getTime();
      const rightTs = new Date(right.createdAt ?? 0).getTime();
      return rightTs - leftTs;
    });
  }

  ngOnDestroy(): void {
    this.bootstrapped = false;
    this.destroy$.next();
    this.destroy$.complete();
  }
}
